import { isSelectionEmpty, toValueWithSelection } from '../helpers';
import Mask, { Direction, ValueWithSelection } from '../Mask';

import { DEFAULT_MATCHABLE_SYMBOLS } from './constants';

export interface MatchableSymbol {
    value: string;
    match: (char: string) => boolean;
    placeholderCharacter?: string;
}

export interface PatternMaskOptions {
    matchableSymbols?: MatchableSymbol[];
    escapeCharacter?: string | null;
    placeholderCharacter?: string;
}

export interface EditableSymbol {
    match: (char: string) => boolean;
    editable: boolean;
    placeholderCharacter?: string;
}
export interface NonEditableSymbol {
    value: string;
    editable: boolean;
    placeholderCharacter?: string;
}

export type PatternSymbol = EditableSymbol | NonEditableSymbol;

export function isEditableSymbol(symbol: PatternSymbol): symbol is EditableSymbol {
    return (symbol as EditableSymbol).match !== undefined;
}

export default class PatternMask extends Mask {
    private pattern: ReadonlyArray<PatternSymbol>;
    private placeholderCharacter: string;

    constructor(
        maskPattern: string,
        {
            matchableSymbols = DEFAULT_MATCHABLE_SYMBOLS,
            escapeCharacter = '\\',
            placeholderCharacter = ' ',
        }: PatternMaskOptions = {}
    ) {
        super();
        const matchableSymbolsMap: { [key: string]: MatchableSymbol } = matchableSymbols.reduce(
            (map, s) => ({ [s.value]: s, ...map }),
            {}
        );
        const { length } = maskPattern;
        const pattern: PatternSymbol[] = [];
        let hasEditableSymbols = false;
        let index = 0;

        while (index !== length) {
            const patternChar = maskPattern[index];

            if (patternChar === escapeCharacter) {
                const escapedSymbol = maskPattern[++index];
                pattern.push({ value: escapedSymbol, editable: false, placeholderCharacter: escapedSymbol });
            } else if (matchableSymbolsMap[patternChar]) {
                hasEditableSymbols = true;
                const matchedSymbol = matchableSymbolsMap[patternChar];
                pattern.push({
                    match: matchedSymbol.match,
                    editable: true,
                    placeholderCharacter: matchedSymbol.placeholderCharacter,
                });
            } else {
                pattern.push({ value: patternChar, editable: false, placeholderCharacter: patternChar });
            }

            index += 1;
        }

        if (!hasEditableSymbols) {
            throw new Error('No editable characters were provided.');
        }

        this.pattern = pattern;
        this.placeholderCharacter = placeholderCharacter;
    }

    conforms(value: string): boolean {
        const { pattern } = this;
        if (value.length !== pattern.length) {
            return false;
        }

        return value.split('').every((char: string, index: number) => {
            const patternSymbol = pattern[index];

            if (patternSymbol != null && isEditableSymbol(patternSymbol)) {
                return patternSymbol.match(char);
            }

            return char === patternSymbol.value;
        });
    }

    input(vs: ValueWithSelection, char: string): ValueWithSelection {
        if (char.length > 1) {
            throw new Error('Cannot input more than 1 character');
        }

        const { start, end } = vs.selection;

        if (
            isSelectionEmpty(vs) &&
            (start === this.pattern.length ||
                (!this.isPlaceholderChar(vs.value, start) && vs.value.length === this.pattern.length))
        ) {
            return vs;
        }

        if (end > start) {
            return this.input(this.remove(vs), char);
        }

        let valueChars = vs.value.split('');
        let index = start;

        // add any non-editable symbols at start
        let patternSymbol = this.pattern[index];
        while (patternSymbol && !isEditableSymbol(patternSymbol)) {
            valueChars[index] = patternSymbol.value;
            patternSymbol = this.pattern[++index];
        }

        if (patternSymbol && patternSymbol.match(char)) {
            // replace empty position
            if (!valueChars[index] || this.isPlaceholderChar(vs.value, index)) {
                valueChars[index++] = char;
                // try to input char in between by shifting right end
            } else {
                const {
                    value: unmaskedValue,
                    selection: { start: unmaskedStart },
                } = this.unmask(toValueWithSelection(valueChars, index));
                const unmaskedChars = unmaskedValue.split('');
                unmaskedChars.splice(unmaskedStart, 0, char);

                // mask and unmask to verify the shift was valid operation
                const newMaskedValueWithSelection = unmaskedChars.reduce(this.input.bind(this), {
                    value: '',
                    selection: { start: 0, end: 0 },
                });
                const { value: unmaskedNewValue } = this.unmask(newMaskedValueWithSelection);

                if (unmaskedChars.join('') === unmaskedNewValue) {
                    valueChars = newMaskedValueWithSelection.value.split('');
                    index++;
                }
            }
        }

        // add any non-editable symbols to end
        patternSymbol = this.pattern[index];
        while (patternSymbol && !isEditableSymbol(patternSymbol)) {
            valueChars[index] = patternSymbol.value;
            patternSymbol = this.pattern[++index];
        }

        return toValueWithSelection(valueChars, index);
    }

    remove(vs: ValueWithSelection, direction: Direction = Direction.Backward): ValueWithSelection {
        if (isSelectionEmpty(vs)) {
            return vs;
        }
        let { start, end } = vs.selection;

        // When deleting the value 1 by 1
        if (end - start === 1) {
            let patternSymbol = this.pattern[start];
            while (patternSymbol && !isEditableSymbol(patternSymbol)) {
                if (direction === Direction.Backward) {
                    start--;
                    patternSymbol = this.pattern[start];
                } else {
                    patternSymbol = this.pattern[end];
                    end++;
                }
            }
        }

        const validatingValue = `${vs.value.slice(0, start)}${vs.value.slice(end)}`;
        if (this.conforms(validatingValue)) {
            return { value: validatingValue, selection: { start, end: start } };
        }

        const valueChars = vs.value.split('');
        while (end > start) {
            const patternSymbol = this.pattern[end - 1];
            if (patternSymbol != null && isEditableSymbol(patternSymbol)) {
                valueChars.splice(end - 1, 1, this.getPlaceholderCharacter(end - 1));
            }
            end--;
        }

        const isValueFilled = valueChars.some(
            (valueChar: string, index: number) => valueChar !== this.getPlaceholderCharacter(index)
        );
        if (!isValueFilled) {
            valueChars.splice(0, valueChars.length);
        }

        return { value: valueChars.join(''), selection: { start, end: start } };
    }

    unmask(vs: ValueWithSelection): ValueWithSelection {
        const { value } = vs;
        const { start: originalStart, end: originalEnd } = vs.selection;
        let unmaskedValue = '';
        let index = 0;
        let finalstart = originalStart;
        let finalEnd = originalEnd;

        while (index < value.length) {
            const patternSymbol = this.pattern[index];
            if (patternSymbol != null && isEditableSymbol(patternSymbol)) {
                unmaskedValue += value[index];
            } else {
                if (index < originalStart) {
                    --finalstart;
                }
                if (index < originalEnd) {
                    --finalEnd;
                }
            }
            ++index;
        }

        return {
            value: unmaskedValue,
            selection: { start: finalstart, end: finalEnd },
        };
    }

    getValueWithPlaceholder(vs: ValueWithSelection): string {
        const { value } = vs;
        return this.pattern
            .map((patternSymbol, index) => {
                if (value[index]) {
                    return value[index];
                }
                return this.getUpperCasedPlaceholderCharacter(index);
            })
            .join('');
    }

    private isPlaceholderChar(value: string, index: number) {
        return value[index] === this.getPlaceholderCharacter(index);
    }

    private getPlaceholderCharacter(index: number) {
        return this.pattern[index]?.placeholderCharacter || this.placeholderCharacter;
    }

    private getUpperCasedPlaceholderCharacter(index: number) {
        return this.getPlaceholderCharacter(index).toUpperCase();
    }
}
